import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import { formatIndianCurrency, formatIndianDateTime } from '../utils/format';

const ProjectRequests = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState(user.role === 'SALES' ? 'ALL' : 'PENDING');
  const [remarks, setRemarks] = useState({});

  const canReview = useMemo(() => user.role === 'ADMIN' || user.role === 'BDM', [user.role]);

  const fetchList = async (nextPage = page) => {
    try {
      setLoading(true);
      const params = { page: nextPage, limit };
      if (status && status !== 'ALL') params.status = status;
      const res = await api.get('/api/project-requests', { params });
      const payload = res.data;
      if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
        setItems(payload.items);
        setTotal(payload.total || 0);
        setPage(payload.page || nextPage);
      } else {
        const arr = Array.isArray(payload) ? payload : [];
        setItems(arr);
        setTotal(arr.length);
        setPage(nextPage);
      }
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load project requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const totalPages = Math.max(Math.ceil(total / limit) || 1, 1);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const approve = async (id) => {
    try {
      await api.post(`/api/project-requests/${id}/approve`, { review_remarks: remarks[id] || '' });
      fetchList(page);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to approve');
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/api/project-requests/${id}/reject`, { review_remarks: remarks[id] || '' });
      fetchList(page);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to reject');
    }
  };

  if (loading) {
    return <div className="text-center p-8">Loading project requests...</div>;
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Project Requests</h1>
          <div className="text-sm text-tertiary mt-1">
            {user.role === 'SALES' ? 'Your submitted requests' : 'Review pending requests and approve to create Projects'}
          </div>
        </div>
        <div className="flex gap-2">
          <select className="glass-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ALL">All</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message mb-6">
          <span className="error-text">{error}</span>
        </div>
      )}

      {items.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <div className="text-secondary">No requests found</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {items.map((r) => (
            <div key={r.id} className="glass-card p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-lg font-semibold truncate">{r.title}</div>
                  <div className="text-sm text-secondary truncate">{r.client_name}</div>
                  <div className="text-xs text-tertiary mt-1">
                    Requested: {formatIndianDateTime(r.requested_at)}
                    {r.requested_by_name ? ` • By: ${r.requested_by_name}` : ''}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`status-badge ${String(r.status || '').toLowerCase()}`}>{r.status}</span>
                  <div className="text-sm font-semibold">{formatIndianCurrency(r.quoted_amount || 0)}</div>
                </div>
              </div>

              {r.notes ? (
                <div className="text-sm text-secondary mt-3" style={{ whiteSpace: 'pre-wrap' }}>{r.notes}</div>
              ) : null}

              {r.reviewed_at ? (
                <div className="text-xs text-tertiary mt-3">
                  Reviewed: {formatIndianDateTime(r.reviewed_at)}{r.reviewed_by_name ? ` • By: ${r.reviewed_by_name}` : ''}
                  {r.review_remarks ? ` • ${r.review_remarks}` : ''}
                </div>
              ) : null}

              {canReview && r.status === 'PENDING' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">Remarks (optional)</label>
                  <input
                    className="glass-input w-full"
                    value={remarks[r.id] || ''}
                    onChange={(e) => setRemarks((prev) => ({ ...prev, [r.id]: e.target.value }))}
                    placeholder="Add remarks for approval/rejection"
                  />
                  <div className="flex gap-2 mt-3">
                    <button type="button" className="glass-button primary" onClick={() => approve(r.id)}>
                      Approve
                    </button>
                    <button type="button" className="glass-button danger" onClick={() => reject(r.id)}>
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-tertiary">Page {page} of {totalPages} ({total} requests)</div>
          <div className="flex gap-2">
            <button type="button" className="glass-button" onClick={() => fetchList(page - 1)} disabled={!canPrev}>
              Prev
            </button>
            <button type="button" className="glass-button" onClick={() => fetchList(page + 1)} disabled={!canNext}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectRequests;
